import { format, parseISO } from 'date-fns';
import { CalendlyExpert } from "../features/experts/types/CalendlyExpert";
import { ExpertRole } from '../features/experts/types/ExpertRole';
import { EMAIL_REGEX, PASSWORD_REGEX } from './Constants';
import { ProductType } from './enums/ProductType';
import { QuestionCategory } from './enums/Surveys';

export const handleDate = (date: string, pattern?: string): string => {
    const parsedDate = parseISO(date);
    return format(parsedDate, pattern ||'dd/MM/yyyy');
}

export const handleExpertRole = (role: string) => {
    switch (role) {
        case ExpertRole.Nutritionist:
            return "Nutrizionista"
        case ExpertRole.Specialist:
            return "Medico Refertante"
        default:
            return ""
    }
}

/**
 * Function giving the days from now to the input date
 * 
 * @param date 
 * @returns number of days rounded
 */
export const handleDaysDifference = (date: string) => {
    const today = new Date()
    const futureDate = new Date(date)
    const differenceInDays = Math.round((futureDate.getTime() - today.getTime()) / (1000 * 3600 * 24))
    return differenceInDays
}

/**
 * Function giving the hours from now to the input date
 * 
 * @param date 
 * @returns number of hours rounded
 */
export const handleHoursDifference = (date: string) => {
    const today = new Date()
    const futureDate = new Date(date)
    const differenceInDays = Math.round((futureDate.getTime() - today.getTime()) / (1000 * 3600))
    return differenceInDays
}

/**
 * Function giving the minutes from now to the input date
 * 
 * @param date 
 * @returns number of minutes rounded
 */
export const handleMinutesDifference = (date: string) => {
    const today = new Date()
    const futureDate = new Date(date)
    const differenceInDays = Math.round((futureDate.getTime() - today.getTime()) / (1000 * 60))
    return differenceInDays
}

/**
 * Function giving the day of the month of the input date
 * 
 * @param date 
 * @returns the number of the day of month
 */
export const handleDayOfMonth = (date: string) => {
    const realDate = new Date(date)
    const day = realDate.getDate()
    return day
}

/**
 * Function giving a 3 letters string of the input date month
 * 
 * @param date 
 * @returns the string representing the month
 */
export const handleMonthString = (date: string) => {
    const realDate = new Date(date)
    const day = realDate.getMonth() + 1
    switch (day) {
        case 1:
            return "gen"
        case 2:
            return "feb"
        case 3:
            return "mar"
        case 4:
            return "apr"
        case 5:
            return "mag"
        case 6:
            return "giu"
        case 7:
            return "lug"
        case 8:
            return "ago"
        case 9:
            return "set"
        case 10:
            return "ott"
        case 11:
            return "nov"
        case 12:
            return "dic"
        default:
            return ""
    }
}

/**
 * Function giving the hour of the input date
 * 
 * @param date 
 * @returns string representing the hout of the input
 */
export const handleHourAndMinutes = (date: string) => {
    const realDate = new Date(date)
    const hour = realDate.getHours()
    const minutes = realDate.getMinutes()
    return `${hour < 10 ? `0${hour.toString()}` : hour}:${minutes < 10 ? `0${minutes.toString()}` : minutes}`
}

export const fromQuestionCategoryToString = (category: QuestionCategory, includeEmoji: boolean = true) => {
    switch (category) {
        case QuestionCategory.PersonalGoals:
            return `${includeEmoji ? "\u{1F642} " : ""}Personali e obiettivi`;
        case QuestionCategory.FamilyHistoryDiseases:
            return `${includeEmoji ? "\u{1F915} " : ""}Familiarità e patologie`;
        case QuestionCategory.LifestyleSports:
            return `${includeEmoji ? "\u{1F3CB} " : ""}Stile di vita e attività sportiva`;
        case QuestionCategory.DietaryHabits:
            return `${includeEmoji ? "\u{1F959} " : ""}Abitudini alimentari`;
        case QuestionCategory.Notes:
            return `${includeEmoji ? "\u{1F3CB} " : ""}Note`;
            case QuestionCategory.MonitoringPreferences:
                return `${includeEmoji ? "⏱️ " : ""}Preferenze di monitoraggio`;
    }
}

export const handleShopifyProductName = (product: ProductType) => {
    switch (product) {
        case ProductType.DnaTest:
            return "🧬 DNA Test Complete"
        case ProductType.BloodTest:
            return "🩸 Analisi degli Aminoacidi"
        case ProductType.ExpertConsult:
            return "👩‍⚕️ Consulto Medico"
        case ProductType.Nutritionist:
            return "👨‍⚕️ Consulto Nutrizionista"
        case ProductType.SubHealth:
        case ProductType.SubHealthDna:
        case ProductType.SubHealthUpsell:
            return "🌀 Percorso Health"
        case ProductType.SubNutrition:
        case ProductType.SubNutritionDna:
            return "🍎 Percorso Nutrition"
        case ProductType.SubMetabolicHealth:
            return "🥙 Percorso Medico e Nutrizionale Completo"
        case ProductType.SubMetabolicHealthLite:
            return "🩺  Percorso Medico di Dimagrimento"
        case ProductType.MetabolicHook:
            return "🚀 Visita Medica Specialistica"
    }
}

/**
 * Function to get a random physician from a list, based on the number of their patients.
 * @param physicians - List of available physicians.
 * @returns A randomly selected nutritionist.
 */
export const getRandomPhysician = (physicians: CalendlyExpert[]) => {
    if (physicians.length === 0) {
        throw new Error("No eligible physicians found.");
    }

    if (physicians.length === 1) {
        return physicians[0];
    }

    // Find the minimum number of patients
    const minPatients = Math.min(...physicians.map(p => p.patients.length));

    // Filter physicians with the minimum patient count
    const leastBusyPhysicians = physicians.filter(p => p.patients.length === minPatients);

    // Return a random one from the least busy
    const index = Math.floor(Math.random() * leastBusyPhysicians.length);
    return leastBusyPhysicians[index];
};

// Validation functions
export const validateEmail = (email: string) => EMAIL_REGEX.test(email) ? "" : "Inserisci un indirizzo mail valido";
export const validatePassword = (password: string) => PASSWORD_REGEX.test(password) ? "" : "La password non rispecchia i criteri di validità";
export const validatePasswordConfirm = (password: string, passwordConfirm: string) => password === passwordConfirm ? "" : "Password diverse";

export const formatPhoneNumber = (prefix: string, number: string): string => {
    // Remove spaces and non-digit characters from the number
    const cleanedNumber = number.replace(/[^\d]/g, "");
    // Combine the prefix and cleaned number
    return `${prefix}${cleanedNumber}`;
};

const answerMappings: Record<string, Record<string, string>> = {
    obiettivo: {
      "Pesoforma": "Pesoforma",
      "Migliore alimentazione in generale": "Alimentazione",
      "Salute gastrointestinale (gonfiore, digestione…)": "Salute Gastrointestinale",
      "Scoprire e agire su intolleranze": "Intolleranze",
      "Longevità e prevenzione a 360°": "Prevenzione",
      "Nutrizione per sport performance": "Sport Performance",
      "Integratori adatti alle mie esigenze": "Integrazione",
      "Migliore stato di salute in generale": "Salute generale",
    },
    acqua: {
      "Meno di mezzo litro": "Meno di mezzo litro",
      "Tra mezzo litro e 1 litro": "Intorno a 1 litro",
      "Tra 1 litro e 1 litro e mezzo": "Intorno a 1 litro e mezzo",
      "Tra 1 litro e mezzo e 2 litri": "Intorno a 2 litri",
      "Oltre 2 litri": "Più di 2 litri",
    },
    sonno: {
      "Buona (7-8 ore, riposante)": "7-8 ore Riposante",
      "Moderata (5-6 ore o alcuni disturbi)": "5-6 ore Disturbato",
      "Scarsa (meno di 5 ore o frequenti disturbi)": "< 5 ore Molto disturbato",
    },
    energia: {
      "Mai": "Livello ottimo",
      "Raramente": "Livello buono",
      "Occasionalmente": "Livello scarso",
      "Frequentemente": "Livello basso",
    },
    digestione: {
      "Mai": "Ottima condizione",
      "Raramente": "Buona condizione",
      "Occasionalmente": "Scarsa condizione",
      "Frequentemente": "Bassa condizione",
    },
    esercizio: {
      "Nessuno": "Scarsa frequenza",
      "0-60 minuti": "Scarsa frequenza",
      "60-150 minuti": "Buona frequenza",
      "Più di 150 minuti": "Ottima frequenza",
    },
    peso: {
        
    }
  };
  
  /**
   * Generic function to map answers dynamically
   * @param category The category key (obiettivo, acqua, sonno, energia, digestione, esercizio)
   * @param answer The answer string to transform
   * @returns Mapped value or original answer if not found
   */
  export const handleAnswer = (category: keyof typeof answerMappings, answer: string): string => {
    if (category === "peso") {
        return `${answer} kg`; // Append "kg" for Peso category
    }
    return answerMappings[category]?.[answer] || answer;
};
